<script setup lang="ts">
import { Head } from '@inertiajs/vue3'
import PageHeading from "@/Components/Headings/PageHeading.vue"
import { PageHeading as PageHeadingTS } from '@/types/PageHeading'
import { useFormatTime } from '@/Composables/useFormatTime'
import { useFormatCurrency } from '@/Composables/useFormatCurrency'
import Tag from '@/Components/Utils/Tag.vue'
import { routeType } from '@/types/route'
import OrdersList from './OrdersList.vue'
import Button from '@/Components/Utils/Button.vue'
import ModalConfirmationDelete from '@/Components/Utils/ModalConfirmationDelete.vue'
import { InvoiceResource } from '@/types/Resources'
import CreateInvoice from '@/Components/Master/Invoices/CreateInvoice.vue'

const props = defineProps<{
    pageHead?: PageHeadingTS
    title?: string
    invoice: InvoiceResource
    route_list: {
        update_order_route: routeType
        reset_order_route: routeType
    }
    payment_method_options: {
        label: string
        value: string
    }[]
}>()
</script>

<template>
    <Head :title="title" />
    <PageHeading :data="pageHead">
        <template #button-delete-invoice="{ action }">
            <ModalConfirmationDelete
                :routeDelete="action.route"
                :title="'Hapus invoice ' + invoice.reference + '?'"
                description="Invoice akan dihapus. Semua order akan dilepas dari invoice ini."
                isFullLoading
            >
                <template #default="{ changeModel }">
                    <Button
                        @click="changeModel()"
                        :style="action.style"
                        :label="action.label"
                        :icon="action.icon"
                        :tooltip="action.tooltip"
                    />
                </template>
            </ModalConfirmationDelete>
        </template>
        <template #afterTitle>
            <Tag
                v-if="invoice.billing_status === 'paid'"
                v-tooltip="useFormatTime(invoice.billing_date, { formatTime: 'hms' })"
                :theme="3"
                size="sm"
                noHoverColor
            >
                <template #label>Lunas</template>
            </Tag>
            <Tag
                v-else-if="invoice.billing_status === 'partial'"
                :theme="7"
                size="sm"
                noHoverColor
            >
                <template #label>Lunas Sebagian</template>
            </Tag>
        </template>

        <template #other>
            <CreateInvoice />
        </template>
    </PageHeading>

    <!-- Info Panel -->
    <div class="mt-4 mx-6 grid grid-cols-2 md:grid-cols-4 gap-3">
        <!-- Tanggal -->
        <div class="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <div class="text-xs text-gray-500 mb-0.5">Tanggal dibuat</div>
            <div class="text-sm font-semibold text-gray-800">
                {{ useFormatTime(invoice.created_at, { formatTime: 'hms'}) }}
            </div>
        </div>

        <!-- Pelanggan -->
        <div class="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <div class="text-xs text-gray-500 mb-0.5">Pelanggan</div>
            <div class="text-sm font-semibold text-gray-800 truncate">
                {{ invoice.customer?.name ?? '-' }}
            </div>
        </div>

        <!-- Sales -->
        <div class="bg-gray-50 border border-gray-200 rounded-lg px-4 py-3">
            <div class="text-xs text-gray-500 mb-0.5">Dibuat oleh</div>
            <div class="text-sm font-semibold text-gray-800 truncate">
                {{ invoice.sales?.name ?? '-' }}
            </div>
        </div>

        <!-- Sisa tagihan -->
        <div
            class="rounded-lg px-4 py-3 border"
            :class="invoice.amount_remaining > 0
                ? 'bg-red-50 border-red-200'
                : 'bg-green-50 border-green-200'"
        >
            <div class="text-xs mb-0.5"
                :class="invoice.amount_remaining > 0 ? 'text-red-400' : 'text-green-500'"
            >
                {{ invoice.amount_remaining > 0 ? 'Sisa Tagihan' : 'Lunas' }}
            </div>
            <div class="text-sm font-bold"
                :class="invoice.amount_remaining > 0 ? 'text-red-600' : 'text-green-600'"
            >
                {{ useFormatCurrency(invoice.amount_remaining > 0 ? invoice.amount_remaining : invoice.final_total) }}
            </div>
        </div>
    </div>

    <!-- Catatan -->
    <div v-if="invoice.note" class="mx-6 mt-2 px-4 py-2 bg-yellow-50 border border-yellow-200 rounded-lg text-sm text-yellow-800">
        <span class="font-medium">Catatan:</span> {{ invoice.note }}
    </div>

    <div class="mt-3 px-6">
        <OrdersList
            :invoice="invoice"
            :updateOrderRoute="props.route_list.update_order_route"
            :resetOrderRoute="props.route_list.reset_order_route"
            :paymentMethodOptions="payment_method_options"
        />
    </div>
</template>